/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { OrderShipments, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import {
    ExpandButtonState,
    getGroups,
    getOrderDetailsPageUrl,
    getOrderInformation,
    getOrderInformationInput,
    getSalesLines,
    getSalesLinesInput,
    IGroup,
    IGroupDelivery,
    IGroups,
    IOrderInformation,
    IOrderHistoryWithSearchAndFiltersOrderListProps,
    SalesLineState
} from '@msdyn365-commerce-modules/order-management';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, format, getPayloadObject, getTelemetryAttributes, Node, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { Observer } from 'mobx-react';
import * as React from 'react';

import { prepareOrderInformationInput, renderOrderInformationAmount } from '../../utils/order-amount-display';

/** Runtime list items may include transaction Id from shipment hydration (OOB uses salesOrder.Id). */
type OrderHistoryListSalesOrder = SalesOrder & { Id?: string };

const componentClassName = 'ms-order-history-with-search-and-filters-order-list';
const orderListItemClassName = `${componentClassName}__sales-order`;
const orderInformationHeaderClassName = `${componentClassName}__sales-order__header`;
const expandProductsButtonClassName = `${componentClassName}__sales-order__expand-products-button`;
const expandProductsButtonWrapperClassName = `${componentClassName}__sales-order__expand-products-button-wrapper`;
const orderDetailButtonClassName = `${componentClassName}__btn-order-details`;
const moduleName = componentClassName;

const OrderInfomationComponent: React.FC<IOrderInformation> = ({
    orderInformationProps,
    placedBy,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {placedBy}
        <div className={orderInformationHeaderClassName}>
            {channelName}
            {count}
        </div>
        {salesId}
        {receiptId}
        {createdDate}
        {renderOrderInformationAmount(amount)}
        {channelReferenceId}
    </Node>
);

const GroupDeliveryComponent: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing, address, trackingInfo }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

const GroupComponent: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <GroupDeliveryComponent {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

const GroupsComponent: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <GroupComponent key={index} {...group} />
        ))}
    </Node>
);

const onExpandProductsCallback = (expandButtonState: ExpandButtonState, productStates: SalesLineState[]) => (): void => {
    for (const productState of productStates) {
        productState.isHidden = false;
    }
    expandButtonState.extraProductsCount = 0;
};

const getSalesOrderKey = (salesOrder: OrderHistoryListSalesOrder): string =>
    salesOrder.SalesId ?? salesOrder.Id ?? String(salesOrder.RecordId ?? '');

const createOrderHistoryWithSearchAndFiltersOrderItemProps = (
    salesOrder: OrderHistoryListSalesOrder,
    props: IOrderHistoryWithSearchAndFiltersOrderListProps
) => {
    const {
        id,
        typeName,
        context,
        moduleProps: { telemetry },
        data: { products, channelIdentities, orgUnitLocations, customer, originators },
        showChannelInfo,
        telemetryContent,
        resources: { freePriceText, detailsAriaLabel, detailsLabel, orderItemLabel, orderItemsLabel },
        context: { actionContext },
        retailMultiplePickUpOptionEnabled,
        channelDeliveryOptionConfig
    } = props;

    const priceContext = {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    };

    const originator = originators?.find(
        o => (salesOrder.SalesId && o.SalesId === salesOrder.SalesId) || (salesOrder.Id && o.TransactionId === salesOrder.Id)
    );

    const salesLinesInput =
        products &&
        getSalesLinesInput(
            props.moduleProps,
            moduleName,
            salesOrder,
            products,
            priceContext,
            undefined,
            undefined,
            undefined,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig
        );

    let salesLines: ReturnType<typeof getSalesLines> | undefined;
    let expandProductsButton: React.ReactNode;

    if (salesLinesInput) {
        const getSalesLinesInputProps = { ...salesLinesInput, imageOnly: true };
        if (!ObjectExtensions.isNullOrUndefined(props.salesLinesLimit)) {
            const extraProductsCount = getSalesLinesInputProps.salesLines.length - (props.salesLinesLimit as number);
            getSalesLinesInputProps.salesLineStates = getSalesLinesInputProps.salesLines.map(
                (salesLine, lineIndex) =>
                    new SalesLineState(`${salesLine.LineId ?? ''}-${lineIndex}`, lineIndex >= (props.salesLinesLimit as number))
            );
            if (extraProductsCount > 0) {
                const expandButtonState = new ExpandButtonState(extraProductsCount);
                expandProductsButton = (
                    <Observer>
                        {() => {
                            const text = format(props.resources.orderHistoryExpandProductsButtonText, expandButtonState.extraProductsCount);
                            return (
                                expandButtonState.extraProductsCount > 0 && (
                                    <Button
                                        className={expandProductsButtonClassName}
                                        title={text}
                                        role='button'
                                        onClick={onExpandProductsCallback(
                                            expandButtonState,
                                            getSalesLinesInputProps.salesLineStates as SalesLineState[]
                                        )}
                                        aria-label={text}
                                    >
                                        {text}
                                    </Button>
                                )
                            );
                        }}
                    </Observer>
                );
            }
        }
        salesLines = getSalesLines(getSalesLinesInputProps);
    }

    expandProductsButton = (
        <>
            {expandProductsButton}
            <div className={expandProductsButtonWrapperClassName} />
        </>
    );

    const groups =
        salesLines &&
        getGroups({
            moduleName,
            salesLines,
            resources: { orderItemLabel, orderItemsLabel },
            retailMultiplePickUpOptionEnabled
        });

    const orderInfomationInput = prepareOrderInformationInput(
        getOrderInformationInput(
            props.moduleProps,
            moduleName,
            salesOrder,
            priceContext,
            channelIdentities,
            orgUnitLocations,
            customer,
            originator,
            showChannelInfo
        )
    );

    const orderDetailsLinkAttributes =
        telemetryContent !== undefined
            ? getTelemetryAttributes(telemetryContent, getPayloadObject('click', telemetryContent, TelemetryConstant.OrderDetails))
            : {};

    const orderDetailsLink = (
        <Button
            href={getOrderDetailsPageUrl(actionContext, salesOrder as OrderShipments)}
            role='button'
            aria-label={
                salesOrder.ChannelReferenceId && detailsAriaLabel
                    ? detailsAriaLabel.replace('{orderId}', salesOrder.ChannelReferenceId)
                    : ''
            }
            className={orderDetailButtonClassName}
            {...orderDetailsLinkAttributes}
        >
            {detailsLabel}
        </Button>
    );

    return {
        orderInfomation: getOrderInformation(orderInfomationInput),
        groups,
        orderDetailsLink,
        expandProductsButton
    };
};

/** Theme list implementation; use from view override (module passes OOB createComponent element). */
export const OrderHistoryWithSearchAndFiltersOrderListView = (props: IOrderHistoryWithSearchAndFiltersOrderListProps): JSX.Element => (
    <Node className={classnames(props.className, componentClassName)}>
        {ArrayExtensions.hasElements(props.data.salesOrders) &&
            props.data.salesOrders.map(salesOrder => {
                const itemProps = createOrderHistoryWithSearchAndFiltersOrderItemProps(salesOrder as OrderHistoryListSalesOrder, props);
                return (
                    <Node className={orderListItemClassName} key={getSalesOrderKey(salesOrder)}>
                        {itemProps.orderInfomation && <OrderInfomationComponent {...itemProps.orderInfomation} />}
                        {itemProps.groups && <GroupsComponent {...itemProps.groups} />}
                        {itemProps.expandProductsButton}
                        {itemProps.orderDetailsLink}
                    </Node>
                );
            })}
    </Node>
);

const OrderHistoryWithSearchAndFiltersOrderList: React.FC<IComponentProps<any>> = props => (
    <OrderHistoryWithSearchAndFiltersOrderListView {...(props as IOrderHistoryWithSearchAndFiltersOrderListProps)} />
);

export const OrderHistoryWithSearchAndFiltersOrderListComponent = msdyn365Commerce.createComponentOverride(
    'OrderHistoryWithSearchAndFiltersOrderListComponent',
    {
        component: OrderHistoryWithSearchAndFiltersOrderList
    }
) as React.FunctionComponent<IOrderHistoryWithSearchAndFiltersOrderListProps>;

export default OrderHistoryWithSearchAndFiltersOrderListComponent;
